import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PackageCard } from "@/components/PackageCard";
import { PackageDetailModal } from "@/components/PackageDetailModal";
import { RankCompare } from "@/components/RankCompare";
import { UsernameDialog } from "@/components/UsernameDialog";
import { DonatePanel } from "@/components/DonatePanel";
import { Skeleton } from "@/components/ui/skeleton";

export const CATEGORIES = {
  keys: { label: "KEYS", title: "Crate Keys", blurb: "Unlock the Ulti and Mythic crates. Rare gear, cosmetics, glory." },
  ranks: { label: "RANKS", title: "Ranks", blurb: "Monthly rank subscriptions. Commands, kits and perks for as long as you're subscribed — cancel anytime." },
  ulti: { label: "ULTI+", title: "ULTI+", blurb: "The membership. Monthly rewards, exclusive perks, zero limits." },
  donate: { label: "DONATE", title: "Donate", blurb: "Fuel the realm. Every donation keeps ULTISMP alive." },
};

export default function StorePage() {
  const { category = "ranks" } = useParams();
  const active = CATEGORIES[category] ? category : "ranks";
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("cancelled")) {
      toast.error("Donation cancelled", { description: "No charge was made." });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setLoading(true);
    api.get("/packages", { params: { category: active } })
      .then((r) => setPackages(r.data))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, [active]);

  const meta = CATEGORIES[active];

  return (
    <main data-testid="store-page" className="relative min-h-screen pt-16">
      <div className="absolute inset-x-0 top-0 h-96 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 60% at 50% 0%, rgba(139,92,246,0.16), transparent 70%)" }} />

      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-28">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-[11px] font-semibold tracking-[0.35em] text-[#A78BFA]">THE STORE — {meta.label}</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white" data-testid="store-category-title">
            {meta.title}
          </h1>
          <p className="mt-4 text-slate-400 text-base sm:text-lg max-w-xl">{meta.blurb}</p>
        </motion.div>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="store-category-tabs">
          {Object.entries(CATEGORIES).map(([key, c]) => (
            <Link
              key={key}
              to={`/store/${key}`}
              data-testid={`store-tab-${key}`}
              className={`px-5 py-2.5 rounded-full text-xs font-bold tracking-[0.15em] border transition-[background-color,border-color,color] duration-300 ${
                key === active
                  ? "bg-[#8B5CF6] border-[#8B5CF6] text-white shadow-[0_0_24px_rgba(139,92,246,0.4)]"
                  : "bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-[#8B5CF6]/50"
              }`}
            >
              {c.label}
            </Link>
          ))}
        </div>

        {active === "donate" && <DonatePanel />}

        {loading ? (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-96 rounded-2xl bg-white/5" />
            ))}
          </div>
        ) : packages.length === 0 ? (
          active !== "donate" && (
            <p className="mt-20 text-center text-slate-500" data-testid="store-empty">No packages in this category yet.</p>
          )
        ) : (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((pkg, i) => (
              <PackageCard key={pkg.id} pkg={pkg} index={i} onPurchase={setPending} compact={active === "keys"} onView={setViewing} />
            ))}
          </div>
        )}

        {active === "ranks" && packages.length >= 2 && (
          <RankCompare ranks={packages} onPurchase={setPending} />
        )}
      </section>

      <UsernameDialog pkg={pending} onClose={() => setPending(null)} />
      <PackageDetailModal
        pkg={viewing}
        onClose={() => setViewing(null)}
        onPurchase={(p) => { setViewing(null); setPending(p); }}
      />
    </main>
  );
}
